import { useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, ArrowRight, CheckCircle2 } from "lucide-react";
import { Pill } from "./bits";
import { PRODUCTS } from "../content/products";
import { trackEvent } from "../lib/api";

const QUESTIONS = [
  {
    id: "need",
    q: "What do you mainly need to know?",
    options: [
      { v: "g", label: "That goods passed through the right places", hint: "Checkpoints, hubs and handover points" },
      { v: "l", label: "Position reports, sent by the label itself", hint: "Autonomous reporting, door to door" },
    ],
  },
  {
    id: "volume",
    q: "Roughly how many shipments per month?",
    options: [
      { v: "g", label: "Hundreds to thousands+", hint: "Per-unit cost dominates" },
      { v: "l", label: "Tens to low hundreds", hint: "Data quality per shipment dominates" },
    ],
  },
  {
    id: "routes",
    q: "Do your shipments mostly move through areas with everyday device activity?",
    options: [
      { v: "g", label: "Yes — cities, hubs and busy transport lanes", hint: "Partner-network detection likely" },
      { v: "l", label: "No — remote or unaccompanied routes", hint: "The label must report for itself" },
    ],
  },
  {
    id: "value",
    q: "What's the value/risk per shipment?",
    options: [
      { v: "g", label: "Moderate — losses hurt but are absorbable", hint: "Volume efficiency matters" },
      { v: "l", label: "High — one loss is a serious event", hint: "Theft, GDP, customer penalties" },
    ],
  },
];

export default function DecisionSelector({ compact = false }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);

  const done = step >= QUESTIONS.length;
  const score = answers.reduce((acc, a) => ({ ...acc, [a]: (acc[a] || 0) + 1 }), {});
  const fit = done ? (score.l > score.g ? "ll100" : score.g > score.l ? "gnts1" : "mixed") : null;
  const fitProduct = fit && fit !== "mixed" ? PRODUCTS[fit] : null;

  const answer = (v) => {
    const next = [...answers, v];
    setAnswers(next);
    setStep(step + 1);
    if (next.length === QUESTIONS.length) {
      trackEvent("selector_completed", { result: next.filter((x) => x === "l").length > next.filter((x) => x === "g").length ? "ll100" : "gnts1" });
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers([]);
  };

  return (
    <div className="rounded-sm border border-border bg-white p-6 sm:p-10" data-testid="decision-selector">
      <div className="mb-8 flex items-center justify-between">
        <p className="font-mono text-xs uppercase tracking-widest text-brand-slate">
          {done ? "Your likely fit" : `Question ${step + 1} of ${QUESTIONS.length}`}
        </p>
        <div className="flex gap-1.5" aria-hidden="true">
          {QUESTIONS.map((_, i) => (
            <span key={i} className={`h-1 w-8 rounded-full ${i < step || done ? "bg-primary" : "bg-border"}`} />
          ))}
        </div>
      </div>

      {!done ? (
        <fieldset>
          <legend className="text-2xl font-semibold tracking-tight text-brand-navy sm:text-3xl">
            {QUESTIONS[step].q}
          </legend>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {QUESTIONS[step].options.map((o, oi) => (
              <button
                key={o.v}
                onClick={() => answer(o.v)}
                data-testid={`selector-q${step + 1}-option-${oi + 1}`}
                className="group rounded-sm border border-border bg-brand-surface p-5 text-left transition-colors duration-150 hover:border-primary hover:bg-white"
              >
                <span className="block text-base font-semibold text-brand-navy group-hover:text-primary">{o.label}</span>
                <span className="mt-1 block text-sm text-brand-slate">{o.hint}</span>
              </button>
            ))}
          </div>
        </fieldset>
      ) : (
        <div data-testid="selector-result">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-1 h-6 w-6 flex-shrink-0 text-brand-lime" aria-hidden="true" />
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-brand-slate">Likely fit — not a verdict</p>
              <h3 className="mt-1 text-2xl font-semibold tracking-tight text-brand-navy sm:text-3xl">
                {fit === "mixed" ? "A mixed estate" : fitProduct.fullName}
              </h3>
              {fitProduct && <div className="mt-3"><Pill label={fitProduct.pill.label} tone={fitProduct.pill.tone} /></div>}
            </div>
          </div>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-brand-slate">
            {fit === "mixed"
              ? "Your answers point in both directions — which usually means a mixed estate: Bluetooth stickers riding the partner detection network for the volume, cellular labels for the shipments that must report autonomously. Many customers run exactly this split."
              : `Your answers lean toward the ${fitProduct.name}. ${fit === "gnts1" ? "You seem to move volume along lanes where the 100M+ partner detection network can do the work — and per-unit cost matters most." : "You seem to need the label to report for itself, over its own cellular connection, with richer positioning inputs."}`}
          </p>
          <p className="mt-4 max-w-2xl rounded-sm border border-amber-300 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900" data-testid="selector-validation-note">
            This tool gives a first steer only — it never declares suitability. Lane profile, materials, airline rules and reporting needs can change the answer, and detection availability depends on participating devices at each place and moment. <strong>Validate with GoAndTrack</strong> before committing.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/request-a-pilot"
              data-testid="selector-cta-validate"
              className="inline-flex items-center gap-2 rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-blue-700"
            >
              Validate with GoAndTrack <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            {fitProduct && (
              <Link
                to={fitProduct.slug}
                data-testid="selector-cta-product"
                className="inline-flex items-center gap-2 rounded-sm border border-brand-navy/20 px-6 py-3 text-sm font-semibold text-brand-navy transition-colors duration-150 hover:border-primary hover:text-primary"
              >
                See the {fitProduct.name}
              </Link>
            )}
            <button
              onClick={reset}
              data-testid="selector-reset"
              className="inline-flex items-center gap-2 rounded-sm px-4 py-3 text-sm font-medium text-brand-slate transition-colors duration-150 hover:text-primary"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Start again
            </button>
          </div>
        </div>
      )}

      {!done && !compact && (
        <p className="mt-8 text-xs text-brand-slate" data-testid="selector-honesty-note">
          Four questions can't capture every lane. The result is a starting hypothesis, always checked with a human.
        </p>
      )}
    </div>
  );
}
